import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { newsTicker, notifications as seededNotifications } from '../data/mock';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;
const READ_STORAGE_KEY = 'complise-read-notifications';

const sourceUrls = {
  BIS: 'https://www.bis.gov.in/',
  WPC: 'https://dot.gov.in/',
  CDSCO: 'https://cdsco.gov.in/',
  FSSAI: 'https://www.fssai.gov.in/',
};

const readStoredIds = () => {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(READ_STORAGE_KEY) || '[]').map(String));
  } catch {
    return new Set();
  }
};

const persistReadIds = (items) => {
  try {
    const readIds = items.filter((item) => !item.unread).map((item) => String(item.id));
    window.localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(readIds));
  } catch {
    // Storage may be unavailable in private browsing; in-memory state still works.
  }
};

const buildLocalNotifications = () => {
  const readIds = readStoredIds();
  return seededNotifications.map((item) => ({
    ...item,
    id: String(item.id),
    unread: !readIds.has(String(item.id)) && item.unread,
    source_url: sourceUrls[item.tag],
  }));
};

const buildLocalTicker = () => newsTicker.map((item, index) => {
  const [title, ...description] = item.text.split(' — ');
  return {
    id: `ticker-${index + 1}`,
    tag: item.tag,
    title,
    desc: description.join(' — '),
  };
});

export const useLiveNews = () => {
  const [notifications, setNotifications] = useState(buildLocalNotifications);
  const [ticker, setTicker] = useState(buildLocalTicker);
  const [loading, setLoading] = useState(Boolean(API));
  const [refreshing, setRefreshing] = useState(false);
  const [connection, setConnection] = useState(API ? 'connecting' : 'ready');
  const [lastUpdated, setLastUpdated] = useState(() => new Date());

  const unread = useMemo(
    () => notifications.reduce((count, item) => count + (item.unread ? 1 : 0), 0),
    [notifications]
  );

  const fetchAll = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);

    if (!API || !navigator.onLine) {
      setConnection(navigator.onLine ? 'ready' : 'offline');
      setLastUpdated(new Date());
      setLoading(false);
      if (manual) window.setTimeout(() => setRefreshing(false), 350);
      return;
    }

    try {
      const [notifRes, tickerRes] = await Promise.all([
        axios.get(`${API}/news`, { params: { category: 'notification', limit: 10 }, timeout: 5000 }),
        axios.get(`${API}/news`, { params: { category: 'ticker', limit: 12 }, timeout: 5000 }),
      ]);

      const apiNotifications = notifRes.data.items || [];
      const apiTicker = tickerRes.data.items || [];
      if (apiNotifications.length) {
        const localReadIds = readStoredIds();
        setNotifications(apiNotifications.map((item) => ({
          ...item,
          id: String(item.id),
          unread: localReadIds.has(String(item.id)) ? false : item.unread,
        })));
      }
      if (apiTicker.length) setTicker(apiTicker);
      setConnection('live');
    } catch {
      // Keep the complete local feed usable when the optional API is unavailable.
      setConnection('ready');
    } finally {
      setLastUpdated(new Date());
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const markRead = useCallback(async (id) => {
    const stringId = String(id);
    setNotifications((previous) => {
      const updated = previous.map((item) => (
        String(item.id) === stringId ? { ...item, unread: false } : item
      ));
      persistReadIds(updated);
      return updated;
    });

    if (API) {
      try {
        await axios.post(`${API}/news/mark-read/${stringId}`, null, { timeout: 5000 });
      } catch {
        // The local read state is authoritative until the API reconnects.
      }
    }
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((previous) => {
      const updated = previous.map((item) => ({ ...item, unread: false }));
      persistReadIds(updated);
      return updated;
    });
  }, []);

  useEffect(() => {
    fetchAll();
    const timer = window.setInterval(() => fetchAll(), 60000);
    const handleOnline = () => fetchAll();
    const handleOffline = () => setConnection('offline');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchAll]);

  return {
    notifications,
    ticker,
    unread,
    loading,
    refreshing,
    connection,
    lastUpdated,
    markRead,
    markAllRead,
    refetch: () => fetchAll({ manual: true }),
  };
};
